import React from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { setSelectedLocation, setSelectedSession } from '@actions/yoga-sauna';
import {
  CardDetails,
  CardHeading,
  ClassesBooknow,
  ClassesCard,
  ClassesData,
  ClassesWrapper,
} from '@components/classes/classes.styled';
import Footer from '@components/footer';
import HeaderNavigation from '@components/header';
import { SvgIcon } from '@components/icon';
import Meta from '@components/meta-tag';
import { Button } from '@components/shared/button';
import { Header, MainContainer } from '@components/shared/common';
import { useYogaSaunaDispatch } from '@context/yoga-sauna';
import { LocationService } from '@models/location';
import useYogaSaunaLocation from '@services/yoga-sauna/use-yoga-sauna-location';
import { parseUrlSlug } from '@utils/common';
import { SERVICE } from '@utils/constants';

const preOpeningPhase: string | undefined =
  process.env.NEXT_PUBLIC_PRE_OPENING_PHASE;

const react = React;
console.log(react);

const ClassesPage: NextPage = () => {
  const router = useRouter();
  const { getSpecificLocation } = useYogaSaunaLocation();
  const yogaSaunaDispatch = useYogaSaunaDispatch();

  const handleBookClick = () => {
    const location = getSpecificLocation(SERVICE.yoga);
    yogaSaunaDispatch(setSelectedLocation(location as LocationService));
    const pathPrefix = 'book-a-class';
    const pathname = `/${pathPrefix}/locations/[name]/available-sessions`;
    const asPath = `/${pathPrefix}/locations/${parseUrlSlug(
      location?.name as string,
    )}/available-sessions`;
    yogaSaunaDispatch(setSelectedSession(null));
    router.push(pathname, asPath);
  };

  const renderBookNowButton = () => (
    <Button
      type="button"
      css={{ width: '100%', marginTop: 20 }}
      onClick={handleBookClick}
    >
      Book Now
    </Button>
  );

  return (
    <>
      <Meta titleSuffix="Classes" />
      <HeaderNavigation
        showBookButton
        showMenuOption
        ispreOpening={!!preOpeningPhase}
        showJoinTheTeamButton={!!preOpeningPhase}
      />
      <MainContainer>
        <ClassesWrapper>
          <Header css={{ paddingTop: '60px', paddingBottom: '0' }}>
            classes
          </Header>
          <CardDetails className="class-subheading">
            We’ll be open Monday-Sunday with early morning, mid-day, and evening
            classes!
          </CardDetails>
        </ClassesWrapper>
        <ClassesData>
          <ClassesCard gridCenter>
            <SvgIcon src="classes1.svg" alt="images" height={89} />
            <CardHeading>Brightside Basics</CardHeading>
            <div className="classes-level">
              <div className="display-justify">
                <div>
                  <b>Experience Level</b>
                </div>
                <div>Introductory</div>
              </div>

              <div className="display-justify">
                <div>
                  <b>Class Length</b>
                </div>
                <div>60-minutes</div>
              </div>

              <div className="display-justify">
                <div>
                  <b>Temperature</b>
                </div>
                <div>89-92 degrees</div>
              </div>

              <p>
                Our introductory vinyasa yoga class offers a gentle yet
                invigorating experience for new and seasoned practitioners
                alike. Poses are taught at a slower pace with an emphasis on
                foundational alignment. If you’re new to yoga, we suggest
                starting here before moving on to our other formats so you’re
                set up for success!
              </p>
            </div>
            <ClassesBooknow>
              {!preOpeningPhase && renderBookNowButton()}
            </ClassesBooknow>
          </ClassesCard>
          <ClassesCard gridCenter>
            <SvgIcon src="classes2.svg" alt="images" height={89} />
            <CardHeading>Power Play</CardHeading>
            <div className="classes-level">
              <div className="display-justify">
                <div>
                  <b>Experience Level</b>
                </div>
                <div>Intermediate</div>
              </div>

              <div className="display-justify">
                <div>
                  <b>Class Length</b>
                </div>
                <div>45-minutes & 60-minutes</div>
              </div>

              <div className="display-justify">
                <div>
                  <b>Temperature</b>
                </div>
                <div>93-96 degrees</div>
              </div>
              <p>
                An intermediate-to-advanced power vinyasa class that offers
                dynamic sequencing, strength, and balancing postures, as well as
                arm balances and inversions. Each class focuses on proper
                alignment and intelligent sequencing progressions. Instructors
                are encouraged to bring their own creative elements to Power
                Play, resulting in unique and inspiring classes.
              </p>
            </div>
            <ClassesBooknow>
              {!preOpeningPhase && renderBookNowButton()}
            </ClassesBooknow>
          </ClassesCard>
          <ClassesCard gridCenter>
            <SvgIcon src="classes3.svg" alt="images" height={89} />
            <CardHeading>Sculpt Sesh </CardHeading>
            <div className="classes-level">
              <div className="display-justify">
                <div>
                  <b>Experience Level</b>
                </div>
                <div>Intermediate</div>
              </div>

              <div className="display-justify">
                <div>
                  <b>Class Length</b>
                </div>
                <div>45-minutes & 60-minutes</div>
              </div>

              <div className="display-justify">
                <div>
                  <b>Temperature</b>
                </div>
                <div>89-92 degrees</div>
              </div>
              <p>
                Our most energetic and athletic format. We take our power
                vinyasa class and add strength training, body weight, and cardio
                exercises to create this fun and vibrant class. Sculpt Sesh is
                designed to increase strength, both physically and mentally.
                Movement is taught to the beat of high-energy music.
              </p>
            </div>
            <ClassesBooknow>
              {!preOpeningPhase && renderBookNowButton()}
            </ClassesBooknow>
          </ClassesCard>
          <ClassesCard gridCenter>
            <SvgIcon src="classes4.svg" alt="images" height={89} />
            <CardHeading>Fusion Flow</CardHeading>
            <div className="classes-level">
              <div className="display-justify">
                <div>
                  <b>Experience Level</b>
                </div>
                <div>All Levels</div>
              </div>

              <div className="display-justify">
                <div>
                  <b>Class Length</b>
                </div>
                <div>60-minutes</div>
              </div>

              <div className="display-justify">
                <div>
                  <b>Temperature</b>
                </div>
                <div>100 degrees</div>
              </div>
              <p>
                Our set sequence format offers our highest heat. Guests will
                practice longer posture holds, breathing exercises, balancing
                series, and floor series. This class is perfect for improving
                lower body strength, upper body mobility, and deep fascial
                release. We guarantee you’ll glow after this flow.
              </p>
            </div>
            <ClassesBooknow>
              {!preOpeningPhase && renderBookNowButton()}
            </ClassesBooknow>
          </ClassesCard>
        </ClassesData>
      </MainContainer>
      <Footer large ispreOpening={!!preOpeningPhase} />
    </>
  );
};

export default ClassesPage;
